import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarIcon, PlusCircle, Search, AlertCircle, UserCheck, Loader2, Building2, X } from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { vendorsAPI } from '@/api/vendors';
import { bitrixAPI } from '@/api/bitrix';

// === CONSTANTS ===

// VENDOR_OPTIONS removed — vendors are now loaded from API via VendorSelect

export const PROJECT_STATUS_OPTIONS = [
  // Начальные этапы
  { value: 'Инициация', label: 'Инициация / Идея' },
  { value: 'Разработка КП', label: 'Разработка КП' },
  { value: 'КП передано Заказчику', label: 'КП передано Заказчику' },
  { value: 'Разработка ТС', label: 'Разработка ТС' },
  { value: 'ТС передана Заказчику', label: 'ТС передана Заказчику' },
  { value: 'Конкурс объявлен', label: 'Конкурс объявлен' },
  // Согласование (блокирующий статус — нет движения сроков, нет ответственности команды)
  { value: 'На согласовании у Заказчика', label: 'На согласовании у Заказчика' },
  // Договорные этапы
  { value: 'Подписание договора', label: 'Подписание договора' },
  { value: 'Договор подписан', label: 'Договор подписан' },
  { value: 'Исполнение договора', label: 'В реализации' },
  // Завершение
  { value: 'Закрытие договора', label: 'Завершён / Работы выполнены' },
  // Финальный статус (проект read-only, фиксация финансов и актов)
  { value: 'Договор закрыт', label: 'Договор закрыт' },
];

export const BLOCK_COLORS = {
  passport: { bg: 'bg-gray-500', text: 'text-white' },
  evaluation: { bg: 'bg-sky-500', text: 'text-white' },
  obligations: { bg: 'bg-yellow-500', text: 'text-white' },
  expenses: { bg: 'bg-orange-500', text: 'text-white' },
  subcontract: { bg: 'bg-purple-500', text: 'text-white' },
  total: { bg: 'bg-green-600', text: 'text-white' },
  risk: { bg: 'bg-red-500', text: 'text-white' },
  alternatives: { bg: 'bg-indigo-500', text: 'text-white' },
};

// === HELPERS ===

/** Format number with spaces: 1234567 -> "1 234 567" */
export const formatMoney = (value) => {
  if (!value && value !== 0) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
};

/** Parse money string back to number: "1 234 567" -> 1234567 */
export const parseMoney = (str) => {
  if (!str) return null;
  const cleaned = str.replace(/\s/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

// === REUSABLE FIELD COMPONENTS ===

export const BlockHeader = ({ title, color }) => (
  <div className={cn('px-4 py-2 rounded-md font-bold text-sm', color.bg, color.text)}>
    {title}
  </div>
);

export const FieldLabel = ({ children, required }) => (
  <div className="bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1.5 rounded whitespace-normal leading-tight">
    {children}{required ? ' *' : ''}
  </div>
);

export const MoneyInput = ({ value, onChange, placeholder = '0', testId, disabled }) => {
  const [displayValue, setDisplayValue] = useState(value ? formatMoney(value) : '');

  useEffect(() => {
    setDisplayValue(value ? formatMoney(value) : '');
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    setDisplayValue(raw);
    onChange(parseMoney(raw));
  };

  const handleBlur = () => {
    if (value !== null && value !== undefined) {
      setDisplayValue(formatMoney(value));
    }
  };

  const handleFocus = () => {
    if (value !== null && value !== undefined && value !== 0) {
      setDisplayValue(String(value));
    }
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={placeholder}
      className="text-right"
      data-testid={testId}
      disabled={disabled}
    />
  );
};

export const PercentInput = ({ value, onChange, testId, disabled }) => (
  <Input
    type="number"
    step="0.01"
    min="0"
    max="100"
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
    placeholder="0"
    className="text-right"
    data-testid={testId}
    disabled={disabled}
  />
);

export const DatePickerField = ({ value, onChange, testId, disabled }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        className={cn(
          "w-full justify-start text-left font-normal text-sm",
          !value && "text-muted-foreground"
        )}
        data-testid={testId}
        disabled={disabled}
      >
        <CalendarIcon className="mr-2 h-3.5 w-3.5" />
        {value ? format(value, 'dd.MM.yyyy', { locale: ru }) : 'Выберите дату'}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar
        mode="single"
        selected={value}
        onSelect={onChange}
        locale={ru}
      />
    </PopoverContent>
  </Popover>
);

// === VENDOR SELECT WITH ADD-NEW CAPABILITY ===

const ADD_NEW_VALUE = '__add_new__';

export const VendorSelect = ({ value, onChange, disabled, testId }) => {
  const [vendors, setVendors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [adding, setAdding] = useState(false);

  const loadVendors = useCallback(async () => {
    try {
      const data = await vendorsAPI.getAll();
      setVendors(data.map((v) => ({ value: v.name, label: v.name })));
    } catch {
      // fallback: empty list
    }
  }, []);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const handleSelectChange = (val) => {
    if (val === ADD_NEW_VALUE) {
      setDialogOpen(true);
      return;
    }
    onChange(val);
  };

  const handleAddVendor = async () => {
    const name = newVendorName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await vendorsAPI.create(name);
      await loadVendors();
      onChange(name);
      setDialogOpen(false);
      setNewVendorName('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Ошибка при добавлении вендора';
      alert(msg);
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <Select value={value} onValueChange={handleSelectChange} disabled={disabled}>
        <SelectTrigger data-testid={testId}>
          <SelectValue placeholder="Выберите вендора" />
        </SelectTrigger>
        <SelectContent>
          {vendors.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
          <SelectItem value={ADD_NEW_VALUE} className="text-blue-600 font-medium">
            <span className="flex items-center gap-1">
              <PlusCircle className="h-3.5 w-3.5" />
              Добавить нового...
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Новый вендор</DialogTitle>
          </DialogHeader>
          <Input
            value={newVendorName}
            onChange={(e) => setNewVendorName(e.target.value)}
            placeholder="Название вендора"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddVendor();
              }
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleAddVendor} disabled={adding || !newVendorName.trim()}>
              {adding ? 'Добавление...' : 'Добавить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// === MANAGER SELECT WITH BITRIX SEARCH ===

export const ManagerSelect = ({
  value,           // { name: string, bitrix_id: string|null }
  onChange,         // (val: { name: string, bitrix_id: string|null }) => void
  disabled,
  testId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bitrixAvailable, setBitrixAvailable] = useState(true);
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState('');

  const searchTimeoutRef = React.useRef(null);
  const wrapperRef = React.useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      setDropdownOpen(false);
      return;
    }
    setSearching(true);
    try {
      const data = await bitrixAPI.searchUsers(q);
      setResults(data.results || []);
      setBitrixAvailable(data.bitrix_available);
      setDropdownOpen(true);
    } catch {
      setResults([]);
      setBitrixAvailable(false);
      setDropdownOpen(true);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchInput = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    // Debounce search
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => doSearch(q), 300);
  };

  const handleSelectResult = (result) => {
    onChange({
      name: result.name,
      bitrix_id: result.bitrix_id || null,
    });
    setSearchQuery('');
    setDropdownOpen(false);
    setManualMode(false);
  };

  const handleManualSubmit = () => {
    const name = manualName.trim();
    if (!name) return;
    onChange({
      name,
      bitrix_id: null,
    });
    setManualMode(false);
    setManualName('');
    setSearchQuery('');
    setDropdownOpen(false);
  };

  const handleClear = () => {
    onChange({ name: '', bitrix_id: null });
    setSearchQuery('');
    setManualMode(false);
  };

  const displayName = value?.name || '';
  const hasBitrixId = !!value?.bitrix_id;
  const isNotSynced = displayName && !hasBitrixId;

  if (disabled) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 border rounded-md text-sm min-h-[36px] bg-gray-50',
          isNotSynced && 'border-yellow-400 bg-yellow-50'
        )}
        data-testid={testId}
      >
        {displayName ? (
          <>
            {hasBitrixId ? (
              <UserCheck className="w-3.5 h-3.5 text-green-600 shrink-0" />
            ) : (
              <AlertCircle className="w-3.5 h-3.5 text-yellow-600 shrink-0" />
            )}
            <span className={isNotSynced ? 'text-yellow-800' : ''}>{displayName}</span>
            {isNotSynced && (
              <span className="text-xs text-yellow-600 ml-auto">(не синхр.)</span>
            )}
          </>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      {/* Selected value display */}
      {displayName && !manualMode ? (
        <div
          className={cn(
            'flex items-center gap-2 px-3 py-2 border rounded-md text-sm min-h-[36px]',
            isNotSynced ? 'border-yellow-400 bg-yellow-50' : 'border-input bg-background'
          )}
        >
          {hasBitrixId ? (
            <UserCheck className="w-3.5 h-3.5 text-green-600 shrink-0" />
          ) : (
            <AlertCircle className="w-3.5 h-3.5 text-yellow-600 shrink-0" />
          )}
          <span className={cn('flex-1 truncate', isNotSynced && 'text-yellow-800')}>
            {displayName}
          </span>
          {isNotSynced && (
            <span className="text-xs text-yellow-600 shrink-0">(не синхр.)</span>
          )}
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 ml-1 shrink-0"
            onClick={handleClear}
          >
            ×
          </button>
        </div>
      ) : (
        <>
          {/* Search input */}
          {!manualMode ? (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={handleSearchInput}
                onFocus={() => { if (results.length > 0) setDropdownOpen(true); }}
                placeholder="Поиск по ФИО или Bitrix ID..."
                className="pl-8 pr-8"
              />
              {searching && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />
              )}
            </div>
          ) : (
            /* Manual input mode */
            <div className="flex gap-2">
              <Input
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="Введите ФИО вручную"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleManualSubmit(); }
                }}
                autoFocus
              />
              <Button
                type="button"
                size="sm"
                onClick={handleManualSubmit}
                disabled={!manualName.trim()}
                className="shrink-0"
              >
                OK
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => { setManualMode(false); setManualName(''); }}
                className="shrink-0"
              >
                ×
              </Button>
            </div>
          )}

          {/* Dropdown results */}
          {dropdownOpen && !manualMode && (
            <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
              {results.length > 0 ? (
                results.map((r, i) => (
                  <button
                    key={`${r.bitrix_id || r.name}-${i}`}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex items-center gap-2 border-b last:border-b-0"
                    onClick={() => handleSelectResult(r)}
                  >
                    {r.source === 'local' ? (
                      <UserCheck className="w-3.5 h-3.5 text-green-600 shrink-0" />
                    ) : (
                      <span className="w-3.5 h-3.5 text-blue-500 text-[10px] font-bold shrink-0 flex items-center justify-center">B</span>
                    )}
                    <span className="flex-1 truncate">{r.name}</span>
                    {r.bitrix_id && (
                      <span className="text-xs text-gray-400 shrink-0">ID: {r.bitrix_id}</span>
                    )}
                  </button>
                ))
              ) : (
                !searching && searchQuery.length >= 2 && (
                  <div className="px-3 py-2 text-sm text-gray-400">Не найдено</div>
                )
              )}

              {/* Manual input option */}
              {!searching && (
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 font-medium flex items-center gap-2 border-t"
                  onClick={() => {
                    setManualMode(true);
                    setManualName(searchQuery);
                    setDropdownOpen(false);
                  }}
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                  Ввести вручную...
                </button>
              )}

              {!bitrixAvailable && (
                <div className="px-3 py-1.5 text-xs text-yellow-600 bg-yellow-50 border-t">
                  Bitrix24 недоступен — показаны только локальные сотрудники
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// === CO-EXECUTOR MULTI-SELECT WITH BITRIX SEARCH ===

export const CoExecutorSelect = ({
  value = [],       // Array of { name: string, bitrix_id: string|null }
  onChange,          // (val: Array<{ name, bitrix_id }>) => void
  disabled,
  testId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bitrixAvailable, setBitrixAvailable] = useState(true);

  const searchTimeoutRef = React.useRef(null);
  const wrapperRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      setDropdownOpen(false);
      return;
    }
    setSearching(true);
    try {
      const data = await bitrixAPI.searchUsers(q);
      setResults(data.results || []);
      setBitrixAvailable(data.bitrix_available);
      setDropdownOpen(true);
    } catch {
      setResults([]);
      setBitrixAvailable(false);
      setDropdownOpen(true);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchInput = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => doSearch(q), 300);
  };

  const handleSelectResult = (result) => {
    const already = value.some(
      (v) => (v.bitrix_id && v.bitrix_id === result.bitrix_id) || v.name === result.name
    );
    if (!already) {
      onChange([...value, { name: result.name, bitrix_id: result.bitrix_id || null }]);
    }
    setSearchQuery('');
    setDropdownOpen(false);
  };

  const handleRemove = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  if (disabled) {
    return (
      <div className="flex flex-wrap gap-1.5 px-3 py-2 border rounded-md text-sm min-h-[36px] bg-gray-50" data-testid={testId}>
        {value.length > 0 ? value.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs">
            {v.bitrix_id ? <UserCheck className="w-3 h-3 text-green-600" /> : null}
            {v.name}
          </span>
        )) : <span className="text-gray-400">—</span>}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      {/* Selected tags */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {value.map((v, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-xs">
              {v.bitrix_id ? <UserCheck className="w-3 h-3 text-green-600" /> : null}
              <span className="truncate max-w-[140px]">{v.name}</span>
              <button type="button" onClick={() => handleRemove(i)} className="text-gray-400 hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={handleSearchInput}
          onFocus={() => { if (results.length > 0) setDropdownOpen(true); }}
          placeholder="Поиск соисполнителя..."
          className="pl-8 pr-8"
        />
        {searching && (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Dropdown */}
      {dropdownOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {results.length > 0 ? (
            results.map((r, i) => {
              const already = value.some(
                (v) => (v.bitrix_id && v.bitrix_id === r.bitrix_id) || v.name === r.name
              );
              return (
                <button
                  key={`${r.bitrix_id || r.name}-${i}`}
                  type="button"
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-b last:border-b-0",
                    already ? "bg-gray-50 text-gray-400 cursor-default" : "hover:bg-gray-100"
                  )}
                  onClick={() => !already && handleSelectResult(r)}
                  disabled={already}
                >
                  {r.source === 'local' ? (
                    <UserCheck className="w-3.5 h-3.5 text-green-600 shrink-0" />
                  ) : (
                    <span className="w-3.5 h-3.5 text-blue-500 text-[10px] font-bold shrink-0 flex items-center justify-center">B</span>
                  )}
                  <span className="flex-1 truncate">{r.name}</span>
                  {r.bitrix_id && <span className="text-xs text-gray-400 shrink-0">ID: {r.bitrix_id}</span>}
                  {already && <span className="text-xs text-gray-400">добавлен</span>}
                </button>
              );
            })
          ) : (
            !searching && searchQuery.length >= 2 && (
              <div className="px-3 py-2 text-sm text-gray-400">Не найдено</div>
            )
          )}
          {!bitrixAvailable && (
            <div className="px-3 py-1.5 text-xs text-yellow-600 bg-yellow-50 border-t">
              Bitrix24 недоступен — показаны только локальные сотрудники
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// === CLIENT SELECT WITH BITRIX WORKGROUP SEARCH ===

export const ClientSelect = ({
  value,           // { name: string, group_id: string|null }
  onChange,         // (val: { name: string, group_id: string|null }) => void
  disabled,
  testId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bitrixAvailable, setBitrixAvailable] = useState(true);
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [creating, setCreating] = useState(false);

  const searchTimeoutRef = React.useRef(null);
  const wrapperRef = React.useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      setDropdownOpen(false);
      return;
    }
    setSearching(true);
    try {
      const data = await bitrixAPI.searchGroups(q);
      setResults(data.results || []);
      setBitrixAvailable(data.bitrix_available);
      setDropdownOpen(true);
    } catch {
      setResults([]);
      setBitrixAvailable(false);
      setDropdownOpen(true);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchInput = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => doSearch(q), 300);
  };

  const handleSelectResult = (result) => {
    onChange({
      name: result.name,
      group_id: result.group_id || null,
    });
    setSearchQuery('');
    setDropdownOpen(false);
    setManualMode(false);
  };

  const handleManualSubmit = () => {
    const name = manualName.trim();
    if (!name) return;
    onChange({
      name,
      group_id: null,
    });
    setManualMode(false);
    setManualName('');
    setSearchQuery('');
    setDropdownOpen(false);
  };

  const handleCreateGroup = async () => {
    const name = manualName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const result = await bitrixAPI.createGroup(name);
      onChange({
        name: result.name || name,
        group_id: result.group_id || null,
      });
      setManualMode(false);
      setManualName('');
      setSearchQuery('');
      setDropdownOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Ошибка при создании группы';
      alert(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleClear = () => {
    onChange({ name: '', group_id: null });
    setSearchQuery('');
    setManualMode(false);
  };

  const displayName = value?.name || '';
  const hasGroupId = !!value?.group_id;
  const isNotSynced = displayName && !hasGroupId;

  if (disabled) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 border rounded-md text-sm min-h-[36px] bg-gray-50',
          isNotSynced && 'border-yellow-400 bg-yellow-50'
        )}
        data-testid={testId}
      >
        {displayName ? (
          <>
            {hasGroupId ? (
              <Building2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
            ) : (
              <AlertCircle className="w-3.5 h-3.5 text-yellow-600 shrink-0" />
            )}
            <span className={isNotSynced ? 'text-yellow-800' : ''}>{displayName}</span>
            {isNotSynced && (
              <span className="text-xs text-yellow-600 ml-auto">(нет в Bitrix)</span>
            )}
          </>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      {/* Selected value display */}
      {displayName && !manualMode ? (
        <div
          className={cn(
            'flex items-center gap-2 px-3 py-2 border rounded-md text-sm min-h-[36px]',
            isNotSynced ? 'border-yellow-400 bg-yellow-50' : 'border-input bg-background'
          )}
        >
          {hasGroupId ? (
            <Building2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
          ) : (
            <AlertCircle className="w-3.5 h-3.5 text-yellow-600 shrink-0" />
          )}
          <span className={cn('flex-1 truncate', isNotSynced && 'text-yellow-800')}>
            {displayName}
          </span>
          {isNotSynced && (
            <span className="text-xs text-yellow-600 shrink-0">(нет в Bitrix)</span>
          )}
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 ml-1 shrink-0"
            onClick={handleClear}
          >
            ×
          </button>
        </div>
      ) : (
        <>
          {/* Search input */}
          {!manualMode ? (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={handleSearchInput}
                onFocus={() => { if (results.length > 0) setDropdownOpen(true); }}
                placeholder="Поиск заказчика (рабочая группа Bitrix)..."
                className="pl-8 pr-8"
              />
              {searching && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />
              )}
            </div>
          ) : (
            /* Manual / Create mode */
            <div className="space-y-2">
              <Input
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="Название заказчика"
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCreateGroup}
                  disabled={creating || !manualName.trim()}
                  className="flex-1"
                >
                  <PlusCircle className="w-3.5 h-3.5 mr-1" />
                  {creating ? 'Создание...' : 'Создать в Bitrix'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleManualSubmit}
                  disabled={!manualName.trim()}
                  className="flex-1"
                >
                  Без Bitrix
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => { setManualMode(false); setManualName(''); }}
                  className="shrink-0"
                >
                  ×
                </Button>
              </div>
            </div>
          )}

          {/* Dropdown results */}
          {dropdownOpen && !manualMode && (
            <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
              {results.length > 0 ? (
                results.map((r, i) => (
                  <button
                    key={`${r.group_id || r.name}-${i}`}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex items-center gap-2 border-b last:border-b-0"
                    onClick={() => handleSelectResult(r)}
                  >
                    {r.source === 'local' ? (
                      r.group_id ? (
                        <Building2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                      ) : (
                        <AlertCircle className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
                      )
                    ) : (
                      <span className="w-3.5 h-3.5 text-blue-500 text-[10px] font-bold shrink-0 flex items-center justify-center">B</span>
                    )}
                    <span className="flex-1 truncate">{r.name}</span>
                    {r.group_id && (
                      <span className="text-xs text-gray-400 shrink-0">ID: {r.group_id}</span>
                    )}
                  </button>
                ))
              ) : (
                !searching && searchQuery.length >= 2 && (
                  <div className="px-3 py-2 text-sm text-gray-400">Не найдено</div>
                )
              )}

              {/* Create new / manual option */}
              {!searching && (
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 font-medium flex items-center gap-2 border-t"
                  onClick={() => {
                    setManualMode(true);
                    setManualName(searchQuery);
                    setDropdownOpen(false);
                  }}
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                  Создать / ввести вручную...
                </button>
              )}

              {!bitrixAvailable && (
                <div className="px-3 py-1.5 text-xs text-yellow-600 bg-yellow-50 border-t">
                  Bitrix24 недоступен — показаны только локальные заказчики
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
